import { createFileRoute } from '@tanstack/react-router';
import { CaseStudyLayout, type CaseStudyContent } from '../components/CaseStudyLayout.js';
import { TravelBufferMockup } from '../components/visuals/TravelBufferMockup.js';
import { IconTrades } from '../components/visuals/IndustryIcons.js';
import { usePageTitle } from '../components/usePageTitle.js';

export const Route = createFileRoute('/for-trades')({
  component: TradesPage,
});

function TradesPage() {
  usePageTitle('Bookings for mobile trades');
  return <CaseStudyLayout content={content} />;
}

const content: CaseStudyContent = {
  eyebrow: 'For mobile trades',
  icon: IconTrades,
  headline: 'Stop booking jobs your day cannot physically fit.',
  headlineEmphasis: 'cannot physically fit',
  subhead:
    'Bookaburra includes travel time, equipment limits, and staff skills before a client sees an available slot.',
  Visual: TravelBufferMockup,
  business: {
    name: 'Two Mowers Gardening',
    location: 'Brisbane · 2 staff · mobile',
    summary:
      'Picture a two-person mobile gardening business in south-east Queensland. Dave (the owner) drives a ute and operates a ride-on mower; Jess handles standard lawns, hedges and tidy-ups. All work is on-site at the client\u2019s address. Every job needs a 60-minute buffer for travel between properties.',
  },
  demoBookingSlug: 'two-mowers',
  dayTimeline: [
    {
      time: '6:45',
      event:
        'Dave checks the day. Four ride-on jobs for him out east; three standard mows for Jess in the same patch.',
      bookaburraResponse:
        'Nothing to do. The day already clusters by postcode \u2014 the travel-aware allocator built it that way.',
    },
    {
      time: '8:10',
      event:
        'A new client books a ride-on mow online and picks "any time Wednesday".',
      bookaburraResponse:
        'Only offers ride-on slots with Dave (Jess doesn\u2019t operate the ride-on). Lands her on Dave\u2019s Wednesday afternoon run.',
    },
    {
      time: '10:30',
      event:
        'A client opens the picker looking for Jess at 11:00, across town from her 10:30 job.',
      bookaburraResponse:
        '11:00 isn\u2019t offered \u2014 the 60-minute travel buffer would be breached. The picker shows 12:00 as Jess\u2019s next viable cross-town slot.',
    },
    {
      time: '12:15',
      event:
        'Rain comes in hard. Three afternoon mows aren\u2019t viable.',
      bookaburraResponse:
        'Sick-day-style reshuffle picks up the three jobs, drafts a move to Thursday morning on the same route. Dave approves before any client is messaged.',
    },
    {
      time: '14:00',
      event:
        'An on-call enquiry comes in for an urgent overgrown-lot tidy.',
      bookaburraResponse:
        'Picker only shows times that fit Dave\u2019s remaining day with the travel buffer respected. Earliest viable: Thursday 10:30.',
    },
    {
      time: '15:40',
      event:
        'A regular tries to move tomorrow\u2019s mow from his reminder SMS.',
      bookaburraResponse:
        'Magic-link reschedule re-runs the travel and skill checks \u2014 he can only land in slots that don\u2019t break the rest of the route.',
    },
  ],
  pains: [
    {
      quote:
        'Two clients booked Dave for back-to-back jobs across town and we found out the morning of.',
      explanation:
        'The calendar counted the job time. It ignored the drive.',
    },
    {
      quote: 'Someone booked Jess for a ride-on mow. Jess doesn\u2019t operate the ride-on.',
      explanation:
        'The booking page treated two staff members as interchangeable when the job needed one specific capability.',
    },
    {
      quote: 'A client moves a job. We end up driving past their place twice in the same day.',
      explanation:
        'A simple reschedule can create extra driving, lateness, and another unhappy client.',
    },
  ],
  solutions: [
    {
      title: 'Stop impossible drives',
      body: (
        <>
          On-site services add a travel buffer to the staff member&rsquo;s calendar.
          If Dave needs an hour between jobs, clients cannot book him sooner.
        </>
      ),
    },
    {
      title: 'Stop booking gear twice',
      body: (
        <>
          If only Dave handles the ride-on mower, ride-on jobs only show
          Dave&rsquo;s available times.
        </>
      ),
    },
    {
      title: 'Stop swapping in the wrong person',
      body: (
        <>
          For a standard mow, Dave or Jess can work. For a ride-on job, only
          Dave can. Bookaburra follows that difference.
        </>
      ),
    },
    {
      title: 'Stop reschedules breaking the day',
      body: (
        <>
          Move a booking and Bookaburra checks travel buffers, skills, and
          existing jobs again before confirming it.
        </>
      ),
    },
  ],
  industrySlug: 'trades',
};
